<script>
  import { onMount } from 'svelte';
  import { Router, Route, Link, navigate } from 'svelte-routing';
  import { isAuthenticated, user, showAlert } from '../lib/stores.js';
  import Analytics from './Analytics.svelte';
  import Vouchers from './Vouchers.svelte';
  import Categories from './Categories.svelte';
  import Reports from './Reports.svelte';
  import Users from './Users.svelte';
  
  // For Svelte 3 compatibility, use local variables and subscribe
  let auth = false;
  let userData = null;
  let activeRoute = '';
  
  // Subscribe to stores
  const unsubscribeAuth = isAuthenticated.subscribe(value => {
    auth = value;
  });
  
  const unsubscribeUser = user.subscribe(value => {
    userData = value;
  });
  
  // Logout function
  function handleLogout() {
    // Clear local storage
    localStorage.removeItem('token');
    
    // Update authentication state
    isAuthenticated.update(() => false);
    user.update(() => null);
    
    // Show message
    showAlert('success', 'Logout berhasil');
    
    // Redirect to login page
    navigate('/login');
  }
  
  // Set active route on load and when route changes
  function updateActiveRoute() {
    activeRoute = window.location.pathname;
  }
  
  // Function to handle navigation
  function navigateTo(path) {
    navigate(path);
    activeRoute = path;
  }
  
  onMount(() => {
    // Check authentication on mount
    if (!auth) {
      navigate('/login');
      return;
    }
    
    // Set initial active route
    updateActiveRoute();
    
    // Listen for route changes
    window.addEventListener('popstate', updateActiveRoute);
    
    // Clean up
    return () => {
      unsubscribeAuth();
      unsubscribeUser();
      window.removeEventListener('popstate', updateActiveRoute);
    };
  });
</script>

<div class="dashboard-container">
  <header class="dashboard-header">
    <div class="header-logo">
      <h1>Sistem Manajemen Voucher</h1>
    </div>
    
    <div class="header-user">
      {#if userData}
        <span class="user-name">Halo, {userData.username}</span>
      {/if}
      <button on:click={handleLogout} class="logout-button">
        Logout
      </button>
    </div>
  </header>
  
  <div class="dashboard-content">
    <aside class="sidebar">
      <nav class="sidebar-nav">
        <ul>
          <li class:active={activeRoute === '/dashboard' || activeRoute === '/'}>
            <a href="/dashboard" on:click|preventDefault={() => navigateTo('/dashboard')}>
              <span class="nav-icon">📊</span>
              Dashboard
            </a>
          </li>
          <li class:active={activeRoute === '/dashboard/analytics'}>
            <a href="/dashboard/analytics" on:click|preventDefault={() => navigateTo('/dashboard/analytics')}>
              <span class="nav-icon">📈</span>
              Analitik
            </a>
          </li>
          <li class:active={activeRoute === '/dashboard/vouchers'}>
            <a href="/dashboard/vouchers" on:click|preventDefault={() => navigateTo('/dashboard/vouchers')}>
              <span class="nav-icon">🎟️</span>
              Voucher
            </a>
          </li>
          <li class:active={activeRoute === '/dashboard/categories'}>
            <a href="/dashboard/categories" on:click|preventDefault={() => navigateTo('/dashboard/categories')}>
              <span class="nav-icon">📁</span>
              Kategori Voucher
            </a>
          </li>
          <li class:active={activeRoute === '/dashboard/reports'}>
            <a href="/dashboard/reports" on:click|preventDefault={() => navigateTo('/dashboard/reports')}>
              <span class="nav-icon">📝</span>
              Laporan
            </a>
          </li>
          {#if userData && userData.role === 'admin'}
            <li class:active={activeRoute === '/dashboard/users'}>
              <a href="/dashboard/users" on:click|preventDefault={() => navigateTo('/dashboard/users')}>
                <span class="nav-icon">👥</span>
                Pengguna
              </a>
            </li>
          {/if}
        </ul>
      </nav>
    </aside>
    
    <main class="main-content">
      {#if activeRoute === '/dashboard/analytics'}
        <Analytics />
      {:else if activeRoute === '/dashboard/vouchers'}
        <Vouchers />
      {:else if activeRoute === '/dashboard/categories'}
        <Categories />
      {:else if activeRoute === '/dashboard/reports'}
        <Reports />
      {:else if activeRoute === '/dashboard/users'}
        <Users />
      {:else}
        <div class="dashboard-welcome">
          <h2>Selamat Datang di Sistem Manajemen Voucher</h2>
          <p>Pilih menu di sidebar untuk mengelola voucher dan kategori</p>
        </div>
      {/if}
    </main>
  </div>
</div>

<style>
  .dashboard-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
  
  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 2rem;
    background-color: #2563eb;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .header-logo h1 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }
  
  .header-user {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .user-name {
    font-weight: 500;
  }
  
  .logout-button {
    padding: 0.5rem 1rem;
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .logout-button:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
  
  .dashboard-content {
    display: flex;
    flex: 1;
  }
  
  .sidebar {
    width: 250px;
    background-color: #1e293b;
    color: #f8fafc;
  }
  
  .sidebar-nav {
    padding: 1rem 0;
  }
  
  .sidebar-nav ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  .sidebar-nav li {
    margin-bottom: 0.25rem;
    border-left: 3px solid transparent;
    transition: border-color 0.2s;
  }
  
  .sidebar-nav li.active {
    border-left-color: #3b82f6;
    background-color: rgba(59, 130, 246, 0.1);
  }
  
  .sidebar-nav a {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    color: #e2e8f0;
    text-decoration: none;
    font-weight: 500;
    transition: background-color 0.2s, color 0.2s;
  }
  
  .sidebar-nav li.active a {
    color: #ffffff;
  }
  
  .sidebar-nav a:hover {
    background-color: rgba(226, 232, 240, 0.1);
  }
  
  .nav-icon {
    margin-right: 0.75rem;
    font-size: 1.25rem;
  }
  
  .main-content {
    flex: 1;
    padding: 2rem;
    background-color: #f1f5f9;
  }
  
  .dashboard-welcome {
    background-color: white;
    border-radius: 8px;
    padding: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .dashboard-welcome h2 {
    margin-top: 0;
    color: #1e3a8a;
  }
  
  @media (max-width: 768px) {
    .dashboard-content {
      flex-direction: column;
    }
    
    .sidebar {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid #e2e8f0;
    }
  }
</style>